import { create } from 'zustand';

/**
 * Toast store — Zustand minimal para notificações transientes.
 *
 * Cobre os casos canônicos:
 *   - `error` 403/422/5xx → feedback de borda vermelha.
 *   - `success` operação concluída.
 *   - `info` aviso neutro.
 *
 * Auto-dismiss após `durationMs` (default 5000). Stack máx 3 visíveis;
 * o mais antigo é descartado quando excede.
 *
 * Acessível fora-de-React via `useToastStore.getState().push(...)` —
 * usado pelo `setup-api-client.ts` para exibir toast em 403 sem
 * dependência circular React ↔ apiClient.
 *
 * Decisão arquitetural (18/Mai/2026 — Item 88):
 *   - Sem dep externa (sonner/react-hot-toast) — princípio P4 do Caderno
 *     (resistência ao over-engineering). Toast component é minimalista,
 *     suficiente para os 3 variants v1.
 *   - Pattern Zustand + bridge externo espelha `auth-store.ts` (Fatia 2).
 *
 * Extensão (22/Ago/2026 — Admin Hub T5, sticky save bar 03c): `actionLabel` +
 * `onAction` opcionais em `ToastItem`/`push`. O toast pós-salvar da barra
 * pegajosa do hub de Administração precisa de um botão "Desfazer" que este
 * store não tinha. Aditivo: os 107 call sites existentes não passam esses
 * campos e continuam com o comportamento idêntico; `ToastContainer` só
 * renderiza o botão quando ambos estão presentes. Deliberadamente NÃO virou
 * um segundo toast dentro do hub — seriam dois caminhos para a mesma ideia
 * (lição `feedback_quantos_caminhos_chegam_aqui`), e o P4 do parágrafo acima
 * justifica não ter dependência externa, não justifica um toast caseiro
 * paralelo a este.
 */

export type ToastVariant = 'success' | 'error' | 'info';

export interface ToastItem {
  id: string;
  variant: ToastVariant;
  title: string;
  description?: string;
  /** ms até auto-dismiss; 0 = não auto-dismissa. */
  durationMs: number;
  /** Rótulo do botão de ação opcional (ex.: "Desfazer" — Admin Hub T5). */
  actionLabel?: string;
  /** Handler da ação opcional. Chamado ao clicar `actionLabel`; o toast fecha em seguida. */
  onAction?: () => void;
}

export interface ToastState {
  toasts: ToastItem[];

  push: (input: {
    variant?: ToastVariant;
    title: string;
    description?: string;
    durationMs?: number;
    actionLabel?: string;
    onAction?: () => void;
  }) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

const MAX_VISIBLE = 3;
const DEFAULT_DURATION_MS = 5000;

function makeId(): string {
  // Math.random é suficiente para id transiente de UI; sem precisão de
  // cripto.
  return `toast_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],

  push: ({
    variant = 'info',
    title,
    description,
    durationMs = DEFAULT_DURATION_MS,
    actionLabel,
    onAction,
  }) => {
    const id = makeId();
    const item: ToastItem = {
      id,
      variant,
      title,
      durationMs,
      ...(description !== undefined && { description }),
      ...(actionLabel !== undefined && { actionLabel }),
      ...(onAction !== undefined && { onAction }),
    };
    set((s) => {
      const next: ToastItem[] = [...s.toasts, item];
      // Mantém apenas os últimos MAX_VISIBLE — drop FIFO.
      const trimmed = next.length > MAX_VISIBLE ? next.slice(next.length - MAX_VISIBLE) : next;
      return { toasts: trimmed };
    });
    if (durationMs > 0) {
      setTimeout(() => get().dismiss(id), durationMs);
    }
    return id;
  },

  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),

  clear: () => set({ toasts: [] }),
}));
